import { isNetworkError } from "./api.js";

/**
 * Check if an error is a real error (not a successful response with empty data)
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a real error, false otherwise
 */
export const isRealError = (error) => {
    if (!error) return false;
    
    // Network errors are always real errors
    if (isNetworkError(error)) {
        return true;
    }
    
    // Check for HTTP status codes - only 4xx and 5xx are errors
    const status = error.response?.status;
    if (status) {
        // 2xx status codes are not errors (even if data is empty)
        if (status >= 200 && status < 300) {
            return false;
        }
        // 4xx and 5xx are errors
        return status >= 400;
    }
    
    // If error has a response object but no status, it's likely an error
    if (error.response) {
        return true;
    }
    
    // If error has a message or is an Error instance, it's likely an error
    if (error.message || error instanceof Error) {
        return true;
    }
    
    return false;
};

/**
 * Get error type from error object
 * @param {Error} error - The error object
 * @returns {string} Error type: 'network', '500', '404', '403', '401', '400', 'unknown'
 */
export const getErrorType = (error) => {
    if (!error) return 'unknown';
    
    // Check for network errors first
    if (isNetworkError(error)) {
        return 'network';
    }
    
    // Check for HTTP status codes
    const status = error.response?.status;
    if (status) {
        if (status === 401) return '401';
        if (status === 403) return '403';
        if (status === 404) return '404';
        if (status >= 500) return '500';
        if (status >= 400) return '400';
    }
    
    return 'unknown';
};

/**
 * Get localized error information based on error type
 * @param {Error} error - The error object
 * @param {string} language - Current language ('am', 'ru', 'en')
 * @param {Object} customMessages - Optional custom messages object
 * @returns {Object} Error info with title, message, and description
 */
export const getErrorInfo = (error, language = 'am', customMessages = {}) => {
    const errorType = getErrorType(error);
    
    // Import translations dynamically to avoid circular dependencies
    // We'll use a function that can be called with translations
    const translations = {
        network: {
            title: {
                am: "Ցանցային սխալ",
                ru: "Ошибка сети",
                en: "Network Error"
            },
            message: {
                am: "Չհաջողվեց կապ հաստատել սերվերի հետ",
                ru: "Не удалось подключиться к серверу",
                en: "Failed to connect to server"
            },
            description: {
                am: "Հնարավոր չէ կապ հաստատել սերվերի հետ: Խնդրում ենք ստուգել ձեր ինտերնետային կապը, թարմացնել էջը կամ դիմել աջակցության բաժին",
                ru: "Не удалось подключиться к серверу. Пожалуйста, проверьте подключение к интернету, обновите страницу или обратитесь в службу поддержки",
                en: "Unable to connect to the server. Please check your internet connection, refresh the page, or contact support"
            }
        },
        '500': {
            title: {
                am: "Սերվերի սխալ",
                ru: "Ошибка сервера",
                en: "Server Error"
            },
            message: {
                am: "Սերվերում տեղի է ունեցել սխալ",
                ru: "Произошла ошибка на сервере",
                en: "An error occurred on the server"
            },
            description: {
                am: "Սերվերում տեղի է ունեցել սխալ: Խնդրում ենք փորձել մի փոքր ուշ, թարմացնել էջը կամ դիմել աջակցության բաժին",
                ru: "Произошла ошибка на сервере. Пожалуйста, попробуйте позже, обновите страницу или обратитесь в службу поддержки",
                en: "An error occurred on the server. Please try again later, refresh the page, or contact support"
            }
        },
        '404': {
            title: {
                am: "Չի գտնվել",
                ru: "Не найдено",
                en: "Not Found"
            },
            message: {
                am: "Պահանջվող ռեսուրսը չի գտնվել",
                ru: "Запрашиваемый ресурс не найден",
                en: "The requested resource was not found"
            },
            description: {
                am: "Պահանջվող ռեսուրսը չի գտնվել: Խնդրում ենք թարմացնել էջը կամ դիմել աջակցության բաժին",
                ru: "Запрашиваемый ресурс не найден. Пожалуйста, обновите страницу или обратитесь в службу поддержки",
                en: "The requested resource was not found. Please refresh the page or contact support"
            }
        },
        '403': {
            title: {
                am: "Արգելված է",
                ru: "Запрещено",
                en: "Forbidden"
            },
            message: {
                am: "Դուք չունեք այս գործողությունը կատարելու իրավունք",
                ru: "У вас нет прав для выполнения этого действия",
                en: "You do not have permission to perform this action"
            },
            description: {
                am: "Դուք չունեք այս գործողությունը կատարելու իրավունք: Խնդրում ենք թարմացնել էջը կամ դիմել աջակցության բաժին",
                ru: "У вас нет прав для выполнения этого действия. Пожалуйста, обновите страницу или обратитесь в службу поддержки",
                en: "You do not have permission to perform this action. Please refresh the page or contact support"
            }
        },
        '401': {
            title: {
                am: "Վավերացման սխալ",
                ru: "Ошибка аутентификации",
                en: "Authentication Error"
            },
            message: {
                am: "Ձեր սեսիան լրացել է",
                ru: "Ваша сессия истекла",
                en: "Your session has expired"
            },
            description: {
                am: "Ձեր սեսիան լրացել է: Խնդրում ենք մուտք գործել կրկին",
                ru: "Ваша сессия истекла. Пожалуйста, войдите снова",
                en: "Your session has expired. Please log in again"
            }
        },
        '400': {
            title: {
                am: "Սխալ հարցում",
                ru: "Неверный запрос",
                en: "Bad Request"
            },
            message: {
                am: "Հարցումը սխալ է",
                ru: "Запрос неверен",
                en: "The request is invalid"
            },
            description: {
                am: "Հարցումը սխալ է: Խնդրում ենք ստուգել մուտքագրած տվյալները և փորձել կրկին",
                ru: "Запрос неверен. Пожалуйста, проверьте введенные данные и попробуйте снова",
                en: "The request is invalid. Please check the entered data and try again"
            }
        },
        unknown: {
            title: {
                am: "Սխալ",
                ru: "Ошибка",
                en: "Error"
            },
            message: {
                am: "Տեղի է ունեցել սխալ",
                ru: "Произошла ошибка",
                en: "An error occurred"
            },
            description: {
                am: "Տեղի է ունեցել սխալ: Խնդրում ենք թարմացնել էջը կամ դիմել աջակցության բաժին",
                ru: "Произошла ошибка. Пожалуйста, обновите страницу или обратитесь в службу поддержки",
                en: "An error occurred. Please refresh the page or contact support"
            }
        }
    };
    
    // Use custom messages if provided, otherwise use default translations
    const errorConfig = customMessages[errorType] || translations[errorType] || translations.unknown;
    
    // Extract error message from response if available
    const serverMessage = error?.response?.data?.message || error?.message;
    
    return {
        type: errorType,
        title: errorConfig.title?.[language] || errorConfig.title?.am || "Սխալ",
        message: serverMessage || errorConfig.message?.[language] || errorConfig.message?.am || "",
        description: errorConfig.description?.[language] || errorConfig.description?.am || ""
    };
};

